import {
  ActorRefFrom,
  and,
  AnyActorRef,
  assign,
  enqueueActions,
  fromPromise,
  not,
  sendTo,
  setup,
  StateFrom,
  stateIn,
  stopChild,
} from 'xstate';
import { employerService } from '~/services/employer/employer.service.machine';
import { notificationState } from '~/services/notifications/notification.machine';
import { providerService } from '~/services/provider/provider.service.machine';
import { PRIVATE_WEBSOCKET_CONFIG, websocketService } from '~/services/websocket';
import { Provider } from '~/types';
import delegationState from '~/utils/delegation';
import { advanceSessionEpoch, getSessionEpoch } from '~/utils/sessionBoundary';
import { alertView } from '../alerts/alertView.machine';
import { OnHideAlert, OnShowAlert } from '../alerts/events';
import { confirmationView } from '../confirmation/confirmationView.machine';
import { ShowConfirmationEvent } from '../confirmation/events';
import { employerRegistrationView } from '../employer/registration-v2/registration-view.machine';
import { marketState } from '../market/marketState.machine';
import { providerRegistrationView } from '../provider/registration-v2/registration-view.machine';
import { vaultState } from '../vault/account/vaultState.machine';
import {
  ALERT_VIEW_STATE,
  CONFIRMATION_VIEW_STATE,
  EMPLOYER_ACCOUNT_STATE,
  EMPLOYER_REGISTRATION_VIEW,
  EMPLOYER_SERVICE,
  NOTIFICATION_STATE,
  PROVIDER_REGISTRATION_VIEW,
  PROVIDER_SERVICE,
  VAULT_STATE,
  WEBSOCKET_SERVICE,
} from '../xstateSystem';
import { fetchUser } from './api';
import {
  buildExitImpersonationUrl,
  clearImpersonationState,
  clearStaleImpersonationOnSignedOut,
  consumeImpersonationHandoff,
  persistImpersonationState,
} from './impersonationHandoff';

export type Role = 'PROVIDER' | 'EMPLOYER' | 'ADMIN' | undefined;

export const MARKET_STATE = EMPLOYER_ACCOUNT_STATE; // I hate this too
export const vaultView = vaultState;
export const marketView = marketState;

export interface Context {
  loading: boolean;
  authenticated: boolean;
  registered: boolean;
  userId?: string; // clerk user id: str
  primaryEmail?: string; // clerk account email: str --> used for registration (maybe use clerk directly?)
  userInfoId?: number; // common.user_info
  providerId?: number; // common.provider.id

  employerUserId?: number; // common.employer_user.id
  roleCode?: Role; // common.user_role.role_code
  error?: Error | null; // system error w/ this machine || catastrophic failure

  // Role the user asked to register as while `userInfoId` was not yet
  // resolved (e.g. the initial user-info fetch failed transiently). Drives
  // the $_RESOLVING_USER_INFO recovery re-fetch before entering registration.
  pendingRegistration?: Exclude<Role, undefined | 'ADMIN'>;

  // services
  websocketService?: ActorRefFrom<typeof websocketService>;

  // child machines
  alertRef?: ActorRefFrom<typeof alertView>;
  notificationsRef?: ActorRefFrom<typeof notificationState>;
  confirmationRef?: ActorRefFrom<typeof confirmationView>;
  vaultRef?: ActorRefFrom<typeof vaultView>; // provider account state
  marketRef?: ActorRefFrom<typeof marketView>; // employerUser account state
  providerRegistrationRef?: ActorRefFrom<typeof providerRegistrationView>;
  employerRegistrationRef?: ActorRefFrom<typeof employerRegistrationView>; // Actor ref for employer registration machine

  // impersonation
  isDelegating: boolean;
  impersonatingProviderId?: number; // ?
  impersonatingProviderName?: string;
  impersonationEndTime?: Date;
  impersonatorProvider?: Provider;
}

export type OnUserLogin = { type: 'ON_USER_LOGIN' };
export type OnUserLogout = { type: 'ON_USER_LOGOUT' };
export type OnSetUserId = { type: 'ON_SET_USER_ID'; userId: string };
export type OnStartEmployerRegistration = { type: 'ON_START_EMPLOYER_REGISTRATION' };
export type OnStartProviderRegistration = { type: 'ON_START_PROVIDER_REGISTRATION' };
export type OnStartImpersonation = {
  type: 'ON_START_IMPERSONATION';
  providerId: number;
  providerName: string;
};
export type OnEndImpersonation = { type: 'ON_END_IMPERSONATION' };
export type OnSessionExpired = { type: 'ON_SESSION_EXPIRED' };
export type OnNavigateEvent = { type: 'ON_NAVIGATE'; to: string };
export type OnBackToRoleSelect = { type: 'ON_BACK_TO_ROLE_SELECT' };
export type OnEmployerInviteAccepted = {
  type: 'ON_EMPLOYER_INVITE_ACCEPTED';
  /** The session the acceptance was started in (`captureSession().epoch`). */
  sessionEpoch?: number;
  output: {
    employerUserId: number;
    employerId: number;
    roleCode: 'EMPLOYER';
  };
};

export type RegistrationOutput = {
  providerId?: number;
  employerUserId?: number;
  employerId?: number;
  roleCode?: Role;
};

export type OnRegistrationComplete = {
  type: 'ON_REGISTRATION_COMPLETE';
  output: RegistrationOutput;
};
export type OnError = { type: 'ON_ERROR'; error: Error };
export type OnViewActorReady = { type: 'ON_VIEW_ACTOR_READY'; viewActor: AnyActorRef };

export type Events =
  | OnUserLogin
  | OnUserLogout
  | OnSetUserId
  | OnStartEmployerRegistration
  | OnStartProviderRegistration
  | OnStartImpersonation
  | OnEndImpersonation
  | OnSessionExpired
  | OnNavigateEvent
  | OnBackToRoleSelect
  | OnEmployerInviteAccepted
  | OnRegistrationComplete
  | OnError
  | OnViewActorReady
  | OnShowAlert
  | OnHideAlert
  | ShowConfirmationEvent;

export type ScopeV2 = {
  context: Context;
  events: Events;
  self?: AnyActorRef;
};

// Type guard helpers for event narrowing in actions
function isOnRegistrationComplete(event: Events): event is OnRegistrationComplete {
  return event.type === 'ON_REGISTRATION_COMPLETE';
}

function isOnEmployerInviteAccepted(event: Events): event is OnEmployerInviteAccepted {
  return event.type === 'ON_EMPLOYER_INVITE_ACCEPTED';
}

function isOnStartImpersonation(event: Events): event is OnStartImpersonation {
  return event.type === 'ON_START_IMPERSONATION';
}

function isOnSetUserId(event: Events): event is OnSetUserId {
  return event.type === 'ON_SET_USER_ID';
}

function isOnShowAlert(event: Events): event is OnShowAlert {
  return event.type === 'ON_SHOW_ALERT';
}

function isOnHideAlert(event: Events): event is OnHideAlert {
  return event.type === 'ON_HIDE_ALERT';
}

function isShowConfirmation(event: Events): event is ShowConfirmationEvent {
  return event.type === 'ON_SHOW_CONFIRMATION';
}
/**
 * Maps a `fetchUser` response onto the user-identity slice of context.
 * Shared by the initial $_FETCHING_USER fetch and the $_RESOLVING_USER_INFO
 * recovery re-fetch so both populate identity fields identically.
 */
type FetchUserOutput = Awaited<ReturnType<typeof fetchUser>>;
function userInfoFromFetchOutput(output: FetchUserOutput): Partial<Context> {
  return {
    loading: false,
    authenticated: true,
    registered: !!output?.roleCode,
    primaryEmail: output?.primaryEmail ?? undefined,
    userInfoId: output?.userInfoId ?? undefined,
    providerId: output?.providerId ?? undefined,
    employerUserId: output?.employerUserId ?? undefined,
    roleCode: output?.roleCode ?? undefined,
  };
}

/**
 * This state machine logic handles the user-login flow logic
 * as users enter the system.
 *
 * After successful login the user should land in the USER_REGISTERED state,
 * where the child-machine accountState (accountRef) is sent the requisite
 * information to handle user interactions throughout the application.
 */
export const userStateV2 = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
  },
  actors: {
    // providerRegistrationRef: providerRegistrationState,
    vaultRef: vaultView,
    alertRef: alertView,
    confirmationRef: confirmationView,
    marketRef: marketView, // used to be "employerstate".
    fetchUser: fromPromise(fetchUser),
    providerRegistrationView: providerRegistrationView,
    employerRegistrationView: employerRegistrationView,
    notificationsRef: notificationState,
    // impersonationProvider
    websocketService,
    providerService: providerService,
    employerService: employerService,
  },
  guards: {
    // A different Clerk user arrived while this one is still on record: a session
    // boundary even though no sign-out was observed in between.
    isIdentityChange: ({ context, event }) =>
      isOnSetUserId(event) && !!context.userId && event.userId !== context.userId,
    // An acceptance is only honoured for the session it was started in, and only
    // for an account that can become an employer: the API refuses a provider or
    // admin (409 ALREADY_REGISTERED_WITH_DIFFERENT_ROLE).
    inviteIsForThisSession: ({ context, event }) => {
      if (!isOnEmployerInviteAccepted(event)) return false;
      if (event.sessionEpoch !== undefined && event.sessionEpoch !== getSessionEpoch()) {
        return false;
      }
      if (!context.userId) return false;
      return context.roleCode !== 'PROVIDER' && context.roleCode !== 'ADMIN';
    },
  },
  actions: {
    setDelegationHeader: ({ context }) => {
      if (context.impersonatingProviderId) {
        delegationState.setActingAsProvider(context.impersonatingProviderId);
      }
    },
    // Only an employer acts as another provider; any other login must not keep a
    // header primed at boot.
    syncDelegationHeader: ({ context }) => {
      delegationState.setActingAsProvider(
        context.roleCode === 'EMPLOYER' ? context.impersonatingProviderId : undefined
      );
    },
    clearDelegationHeader: () => {
      delegationState.setActingAsProvider(undefined);
    },
    persistCurrentImpersonationState: ({ context }) => {
      persistImpersonationState({
        providerId: context.impersonatingProviderId,
        providerName: context.impersonatingProviderName,
        ownerUserId: context.userId,
      });
    },
    clearPersistedImpersonationState: () => {
      clearImpersonationState();
    },
    clearStaleImpersonationOnSignedOut: () => {
      clearStaleImpersonationOnSignedOut();
    },
    advanceSession: () => {
      advanceSessionEpoch();
    },
    // The alert and confirmation actors outlive the session, but what they hold
    // (text, filenames, callbacks that act on the account) does not.
    resetAccountUi: ({ context }) => {
      context.alertRef?.send({ type: 'ON_RESET_ALERTS' });
      context.confirmationRef?.send({ type: 'ON_RESET_CONFIRMATION' });
    },
    /**
     * Ends the account session. Spawned actors are children of this actor, so they
     * outlive the state that spawned them and `actor.stop()` refuses them: each is
     * stopped with `stopChild`. Safe from any state (a missing child is a no-op).
     */
    teardownAccountSession: enqueueActions(({ enqueue }) => {
      enqueue('advanceSession');
      enqueue('clearDelegationHeader');
      enqueue('clearPersistedImpersonationState');
      enqueue('resetAccountUi');
      enqueue.stopChild(VAULT_STATE);
      enqueue.stopChild(MARKET_STATE);
      enqueue.stopChild(PROVIDER_SERVICE);
      enqueue.stopChild(EMPLOYER_SERVICE);
      enqueue.stopChild(NOTIFICATION_STATE);
      enqueue.stopChild(WEBSOCKET_SERVICE);
      enqueue.assign({
        loading: false,
        authenticated: false,
        registered: false,
        userId: undefined,
        primaryEmail: undefined,
        userInfoId: undefined,
        providerId: undefined,
        employerUserId: undefined,
        roleCode: undefined,
        error: null,
        pendingRegistration: undefined,
        websocketService: undefined,
        notificationsRef: undefined,
        vaultRef: undefined,
        marketRef: undefined,
        providerRegistrationRef: undefined,
        employerRegistrationRef: undefined,
        isDelegating: false,
        impersonatingProviderId: undefined,
        impersonatingProviderName: undefined,
        impersonationEndTime: undefined,
        impersonatorProvider: undefined,
      });
    }),
  },
}).createMachine({
  context: ({ spawn }) => {
    const alertRef = spawn('alertRef', {
      id: ALERT_VIEW_STATE,
      systemId: ALERT_VIEW_STATE,
      input: {},
    }) as ActorRefFrom<typeof alertView>;

    const confirmationRef = spawn('confirmationRef', {
      id: CONFIRMATION_VIEW_STATE,
      systemId: CONFIRMATION_VIEW_STATE,
      input: {},
    });
    return {
      loading: false,
      authenticated: false,
      registered: false,
      error: null,
      alertRef,
      confirmationRef,

      /**
       * Trying to figure out impersonation/Delegating
       */
      isDelegating: false,
      impersonatingProviderId: undefined,
      impersonatingProviderName: undefined,
      impersonationEndTime: undefined,
      impersonatorProvider: undefined,
    };
  },
  initial: '$_ANONYMOUS',
  on: {
    // A session ends in any state, not only a fully authenticated one.
    ON_USER_LOGOUT: {
      target: '.$_SIGNING_OUT',
    },
    ON_SET_USER_ID: {
      guard: 'isIdentityChange',
      target: '.$_SWITCHING_IDENTITY',
    },
    ON_SHOW_ALERT: {
      actions: ({ context, event }) => {
        if (isOnShowAlert(event)) {
          context.alertRef?.send(event);
        }
      },
    },
    ON_HIDE_ALERT: {
      actions: ({ context, event }) => {
        if (isOnHideAlert(event)) {
          context.alertRef?.send(event);
        }
      },
    },
    ON_SHOW_CONFIRMATION: {
      actions: ({ context, event }) => {
        if (isShowConfirmation(event)) {
          if (context.confirmationRef) {
            context.confirmationRef.send(event);
          } else {
            console.error('[userState] confirmationRef is null/undefined');
          }
        }
      },
    },
    ON_EMPLOYER_INVITE_ACCEPTED: [
      {
        // Already an employer: the running market and services stay as they are.
        guard: and([
          'inviteIsForThisSession',
          stateIn({ $_AUTHENTICATED: '$_AUTHENTICATED_EMPLOYER' }),
        ]),
        actions: assign({
          employerUserId: ({ event }) =>
            isOnEmployerInviteAccepted(event) ? event.output.employerUserId : undefined,
        }),
      },
      {
        guard: and([
          'inviteIsForThisSession',
          not(stateIn({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })),
        ]),
        target: '.$_AUTHENTICATED.$_AUTHENTICATED_EMPLOYER',
        actions: [
          assign({
            loading: () => false,
            authenticated: () => true,
            registered: () => true,
            providerId: () => undefined,
            employerUserId: ({ event }) =>
              isOnEmployerInviteAccepted(event) ? event.output.employerUserId : undefined,
            roleCode: () => 'EMPLOYER' as const,
          }),
        ],
      },
    ],
  },
  states: {
    $_ANONYMOUS: {
      on: {
        ON_USER_LOGIN: {
          target: '$_AUTHENTICATING',
        },
        // A confirmed signed-out page load: no actors to stop, but anything an
        // earlier session left behind must not be picked up by the next sign-in.
        ON_USER_LOGOUT: {
          actions: ['clearStaleImpersonationOnSignedOut'],
        },
      },
    },
    $_AUTHENTICATING: {
      on: {
        ON_SET_USER_ID: {
          target: '$_FETCHING_USER',
          actions: assign({
            userId: ({ event }) => (isOnSetUserId(event) ? event.userId : ''),
          }),
        },
      },
    },
    $_FETCHING_USER: {
      invoke: {
        src: 'fetchUser',
        input: ({ context }) => ({ clerkUserId: context.userId ?? '' }),
        onDone: {
          target: '$_DETERMINE_ROLE',
          actions: [
            assign({
              loading: () => false,
              authenticated: () => true,
              primaryEmail: ({ event }) => event.output?.primaryEmail ?? undefined,
              registered: ({ event }) => !!event.output?.roleCode || false,
              userInfoId: ({ event }) => event.output?.userInfoId ?? undefined,
              providerId: ({ event }) => event.output?.providerId ?? undefined,
              employerUserId: ({ event }) => event.output?.employerUserId ?? undefined,
              roleCode: ({ event }) => event.output?.roleCode ?? undefined,
              impersonatingProviderId: ({ context }) =>
                consumeImpersonationHandoff(context.userId)?.providerId,
              impersonatingProviderName: ({ context }) =>
                consumeImpersonationHandoff(context.userId)?.providerName,
            }),
            'syncDelegationHeader',
          ],
        },
        onError: {
          target: '$_CHOOSE_ROLE',
          actions: [
            assign({
              loading: () => false,
              authenticated: () => true, // User is authenticated but has no role yet
            }),
          ],
        },
      },
    },
    $_DETERMINE_ROLE: {
      always: [
        // Provider role -> authenticated with vault
        {
          guard: ({ context }) => context.roleCode === 'PROVIDER',
          target: '$_AUTHENTICATED.$_AUTHENTICATED_PROVIDER',
          actions: [],
        },
        // Employer role with active impersonation -> authenticated with impersonation
        {
          guard: ({ context }) =>
            context.roleCode === 'EMPLOYER' && context.impersonatingProviderId != null,
          target: '$_AUTHENTICATED.$_AUTHENTICATED_IMPERSONATING',
        },
        // Employer role -> authenticated WITHOUT vault
        {
          guard: ({ context }) => context.roleCode === 'EMPLOYER',
          target: '$_AUTHENTICATED.$_AUTHENTICATED_EMPLOYER',
        },
        // Admin role -> authenticated WITHOUT vault
        {
          guard: ({ context }) => context.roleCode === 'ADMIN',
          target: '$_AUTHENTICATED.$_AUTHENTICATED_ADMIN',
        },
        // Not recognized or no role -> choose role
        {
          target: '$_CHOOSE_ROLE',
        },
      ],
    },
    $_CHOOSE_ROLE: {
      // Entering role selection clears any prior pending-registration intent.
      entry: assign({ pendingRegistration: () => undefined }),
      on: {
        // If the initial user fetch failed, the user can reach this screen
        // without a userInfoId. Registration requires one, so re-resolve it
        // before entering the registration flow instead of crashing.
        ON_START_EMPLOYER_REGISTRATION: [
          {
            guard: ({ context }) => !!context.userInfoId,
            target: '$_EMPLOYER_REGISTRATION',
          },
          {
            target: '$_RESOLVING_USER_INFO',
            actions: assign({ pendingRegistration: () => 'EMPLOYER' as const }),
          },
        ],
        ON_START_PROVIDER_REGISTRATION: [
          {
            guard: ({ context }) => !!context.userInfoId,
            target: '$_PROVIDER_REGISTRATION',
          },
          {
            target: '$_RESOLVING_USER_INFO',
            actions: assign({ pendingRegistration: () => 'PROVIDER' as const }),
          },
        ],
      },
    },
    // Recovery state: the user started registration but userInfoId was missing
    // (a transient user-info fetch failure dropped them into $_CHOOSE_ROLE).
    // Re-fetch, then continue into the registration flow they requested.
    $_RESOLVING_USER_INFO: {
      invoke: {
        src: 'fetchUser',
        input: ({ context }) => ({ clerkUserId: context.userId ?? '' }),
        onDone: [
          // A successful retry can return an already-registered user (the
          // initial failure was transient). Route them through the normal
          // authenticated branch rather than back into registration.
          {
            guard: ({ event }) => !!event.output?.roleCode,
            target: '$_DETERMINE_ROLE',
            actions: assign(({ event }) => userInfoFromFetchOutput(event.output)),
          },
          {
            guard: ({ context, event }) =>
              !!event.output?.userInfoId &&
              !event.output?.roleCode &&
              context.pendingRegistration === 'EMPLOYER',
            target: '$_EMPLOYER_REGISTRATION',
            actions: assign(({ event }) => userInfoFromFetchOutput(event.output)),
          },
          {
            guard: ({ context, event }) =>
              !!event.output?.userInfoId &&
              !event.output?.roleCode &&
              context.pendingRegistration === 'PROVIDER',
            target: '$_PROVIDER_REGISTRATION',
            actions: assign(({ event }) => userInfoFromFetchOutput(event.output)),
          },
          // Re-fetch returned no userInfoId — return to role selection so the
          // user can retry rather than entering registration in a bad state.
          { target: '$_CHOOSE_ROLE' },
        ],
        // Still failing (e.g. token not ready). Fall back to role selection;
        // the user can try again without the app erroring out.
        onError: { target: '$_CHOOSE_ROLE' },
      },
    },
    $_PROVIDER_REGISTRATION: {
      exit: [
        assign({
          providerRegistrationRef: () => undefined,
        }),
      ],
      invoke: {
        id: PROVIDER_REGISTRATION_VIEW,
        src: 'providerRegistrationView',
        input: ({ context }) => {
          if (!context.userInfoId) {
            throw new Error('userInfoId must be set when entering $_PROVIDER_REGISTRATION state');
          }
          return {
            userInfoId: context.userInfoId,
            primaryEmail: context.primaryEmail || '',
          };
        },
        onDone: {
          target: '$_AUTHENTICATED.$_AUTHENTICATED_PROVIDER',
          actions: [
            assign({
              loading: () => false,
              authenticated: () => true,
              providerId: ({ event }) => {
                return event.output?.providerId;
              },
              employerUserId: ({ event }) => event.output?.employerUserId,
              roleCode: ({ event }) => event.output?.roleCode,
              registered: () => true,
            }),
          ],
        },
      },
      on: {
        ON_REGISTRATION_COMPLETE: {
          target: '$_AUTHENTICATED.$_AUTHENTICATED_PROVIDER',
          actions: [
            assign({
              loading: () => false,
              authenticated: () => true,
              userInfoId: ({ event }) => event.output.providerId ?? event.output.employerUserId,
              providerId: ({ event }) => event.output.providerId,
              employerUserId: ({ event }) => event.output.employerUserId,
              roleCode: ({ event }) => event.output.roleCode,
              registered: () => true,
            }),
          ],
        },
        ON_ERROR: {
          target: '$_CHOOSE_ROLE',
          actions: ({ event }) => {
            console.error('Provider registration failed:', event.error);
          },
        },
        ON_BACK_TO_ROLE_SELECT: {
          target: '$_CHOOSE_ROLE',
        },
      },
    },
    $_EMPLOYER_REGISTRATION: {
      entry: [],
      invoke: {
        id: EMPLOYER_REGISTRATION_VIEW,
        src: 'employerRegistrationView',
        input: ({ context }) => {
          if (!context.userInfoId) {
            throw new Error('userInfoId must be set when entering $_EMPLOYER_REGISTRATION state');
          }
          return {
            userInfoId: context.userInfoId,
            primaryEmail: context.primaryEmail || '',
          };
        },
        onDone: {
          target: '$_AUTHENTICATED.$_AUTHENTICATED_EMPLOYER',
          actions: [
            assign({
              loading: () => false,
              authenticated: () => true,
              providerId: () => undefined,
              employerUserId: ({ event }) => event.output?.employerUserId,
              roleCode: ({ event }) => event.output?.roleCode,
              registered: () => true,
            }),
          ],
        },
      },
      on: {
        ON_BACK_TO_ROLE_SELECT: {
          target: '$_CHOOSE_ROLE',
        },
        ON_REGISTRATION_COMPLETE: {
          target: '$_AUTHENTICATED.$_AUTHENTICATED_EMPLOYER',
          actions: [
            assign({
              employerUserId: ({ event }) =>
                isOnRegistrationComplete(event) ? event.output.employerUserId : undefined,
              roleCode: () => 'EMPLOYER' as const,
              registered: () => true,
            }),
          ],
        },
      },
    },
    $_AUTHENTICATED: {
      entry: [
        assign({
          websocketService: ({ self, spawn }) => {
            const existing = self.system.get(WEBSOCKET_SERVICE);
            if (existing) {
              return existing;
            }
            return spawn('websocketService', {
              id: WEBSOCKET_SERVICE,
              systemId: WEBSOCKET_SERVICE,
              input: {
                config: PRIVATE_WEBSOCKET_CONFIG,
              },
            });
          },
          notificationsRef: ({ self, spawn, context }) => {
            // Check if actor already exists before spawning
            const existing = self.system.get(NOTIFICATION_STATE);
            if (existing) {
              return existing;
            }
            return spawn('notificationsRef', {
              id: NOTIFICATION_STATE,
              systemId: NOTIFICATION_STATE,
              input: {
                userId: context.userId,
                userRole: context.roleCode,
              },
            });
          },
        }),
        sendTo(NOTIFICATION_STATE, { type: 'ON_LOAD' }),
      ],
      initial: '$_AUTHENTICATED_PROVIDER',
      states: {
        $_AUTHENTICATED_PROVIDER: {
          entry: [
            // A provider service is fixed to one provider id; one for any other id is
            // not this account's and is replaced rather than reused.
            enqueueActions(({ enqueue, self, context }) => {
              const existing = self.system.get(PROVIDER_SERVICE);
              if (existing && existing.getSnapshot().context.providerId !== context.providerId) {
                enqueue.stopChild(PROVIDER_SERVICE);
              }
            }),
            assign({
              vaultRef: ({ self, spawn, context }) => {
                // Check if providerService already exists
                let providerService = self.system.get(PROVIDER_SERVICE);
                if (!providerService) {
                  providerService = spawn('providerService', {
                    id: PROVIDER_SERVICE,
                    systemId: PROVIDER_SERVICE,
                    input: {
                      providerId: context.providerId,
                    },
                  }) as ActorRefFrom<typeof providerService>;
                }

                // Always spawn fresh vaultRef for new session
                return spawn('vaultRef', {
                  id: VAULT_STATE,
                  systemId: VAULT_STATE,
                  input: {
                    userId: context.userId,
                    userInfoId: context.userInfoId,
                    providerId: context.providerId,
                    providerService: providerService,
                  },
                });
              },
              marketRef: ({ spawn, context }) => {
                // Spawned actors outlive the state that spawned them; the session
                // teardown stops this one.
                return spawn('marketRef', {
                  id: MARKET_STATE,
                  input: {
                    isProvider: true,
                    userInfoId: context.userInfoId,
                    roleCode: context.roleCode,
                  },
                });
              },
            }),
          ],
        },
        $_AUTHENTICATED_EMPLOYER: {
          entry: [
            assign({
              marketRef: ({ self, spawn, context }) => {
                // Re-entering within one session (back from impersonation) keeps the
                // running market: its descendants hold fixed system ids, so a second
                // one cannot be spawned beside it.
                const running = context.marketRef;
                if (running && running.getSnapshot().status === 'active') {
                  return running;
                }

                // Check if employerService already exists
                let employerService = self.system.get(EMPLOYER_SERVICE);
                if (!employerService) {
                  employerService = spawn('employerService', {
                    id: EMPLOYER_SERVICE,
                    systemId: EMPLOYER_SERVICE,
                    input: {},
                  });
                }

                const marketRef = spawn('marketRef', {
                  id: MARKET_STATE,
                  input: {
                    userInfoId: context.userInfoId,
                    providerId: context.providerId,
                    roleCode: context.roleCode,
                    employerService: employerService,
                  },
                });

                return marketRef;
              },
            }),
          ],
          on: {
            ON_START_IMPERSONATION: {
              target: '$_AUTHENTICATED_IMPERSONATING',
              actions: [
                assign({
                  vaultRef: () => undefined,
                  impersonatingProviderId: ({ event }) =>
                    isOnStartImpersonation(event) ? event.providerId : undefined,
                  impersonatingProviderName: ({ event }) =>
                    isOnStartImpersonation(event) ? event.providerName : undefined,
                }),
              ],
            },
          },
        },
        $_AUTHENTICATED_ADMIN: {
          entry: [],
        },
        $_AUTHENTICATED_IMPERSONATING: {
          // Employer is impersonating a provider - spawn temporary vault
          entry: [
            'setDelegationHeader',
            'persistCurrentImpersonationState',
            assign({
              impersonationEndTime: () => {
                const timeoutMs = 60 * 60 * 1000; // Default 1 hour
                return new Date(Date.now() + timeoutMs);
              },
              vaultRef: ({ spawn, context }) => {
                const impersonatingProviderId = context.impersonatingProviderId;
                if (impersonatingProviderId == null) {
                  throw new Error(
                    'impersonatingProviderId must be set when entering $_AUTHENTICATED_IMPERSONATING'
                  );
                }
                const ps = spawn('providerService', {
                  id: PROVIDER_SERVICE,
                  systemId: PROVIDER_SERVICE,
                  input: { providerId: impersonatingProviderId },
                }) as ActorRefFrom<typeof providerService>;
                return spawn('vaultRef', {
                  id: VAULT_STATE,
                  systemId: VAULT_STATE,
                  input: {
                    userId: context.userId,
                    userInfoId: context.userInfoId,
                    providerId: impersonatingProviderId,
                    providerService: ps,
                  },
                });
              },
            }),
          ],
          on: {
            ON_END_IMPERSONATION: {
              target: '$_AUTHENTICATED_EMPLOYER',
              actions: [
                // Queue the cross-host navigation FIRST so any teardown that
                // follows (stopChild, etc.) can't prevent the assignment from
                // being scheduled, and so a relative path doesn't strand the
                // user on the vault host where HLX routes aren't mounted.
                () => {
                  window.location.assign(buildExitImpersonationUrl());
                },
                'clearDelegationHeader',
                'clearPersistedImpersonationState',
                stopChild(VAULT_STATE),
                stopChild(PROVIDER_SERVICE),
                assign({
                  vaultRef: () => undefined,
                  impersonatingProviderId: () => undefined,
                  impersonatingProviderName: () => undefined,
                  impersonationEndTime: () => undefined,
                }),
              ],
            },
            ON_SESSION_EXPIRED: {
              target: '$_AUTHENTICATED_EMPLOYER',
              actions: [
                () => {
                  window.location.assign(buildExitImpersonationUrl());
                },
                'clearDelegationHeader',
                'clearPersistedImpersonationState',
                stopChild(VAULT_STATE),
                stopChild(PROVIDER_SERVICE),
                assign({
                  vaultRef: () => undefined,
                  impersonatingProviderId: () => undefined,
                  impersonatingProviderName: () => undefined,
                  impersonationEndTime: () => undefined,
                }),
              ],
            },
          },
        },
      },
    },
    $_SIGNING_OUT: {
      entry: ['teardownAccountSession'],
      always: {
        target: '$_ANONYMOUS',
      },
    },
    // A different Clerk user arrived without a sign-out in between. Transient: it
    // ends the previous session exactly as sign-out does and fetches the new user.
    $_SWITCHING_IDENTITY: {
      entry: [
        'teardownAccountSession',
        assign({
          userId: ({ event }) => (isOnSetUserId(event) ? event.userId : undefined),
          loading: () => true,
        }),
      ],
      always: {
        target: '$_FETCHING_USER',
      },
    },
  },
});

export type UserStateV2Value = StateFrom<typeof userStateV2>['value'];
